import { useState, type FormEvent } from "react";
import type { DirectoryUser } from "../shared/types";
import { splitList } from "./admin-model";
import { useAdminSession } from "./admin-query";
import { Modal } from "./Modal";
import type { UserDraft } from "./types";

interface Props {
  user?: DirectoryUser;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (draft: UserDraft) => void;
}

function initialDraft(user?: DirectoryUser): UserDraft {
  return {
    username: user?.username ?? "",
    email: user?.email ?? "",
    name: user?.name ?? "",
    password: "",
    status: user?.status ?? "active",
    groups: user?.groups ?? [],
  };
}

export function UserDialog({ user, busy, error, onClose, onSave }: Props) {
  const { passwordPolicy } = useAdminSession();
  const [draft, setDraft] = useState(() => initialDraft(user));
  const [groups, setGroups] = useState(user?.groups.join(", ") ?? "");
  const minimumPasswordLength =
    passwordPolicy.overrides.find(
      (entry) => entry.username === draft.username.trim().toLowerCase(),
    )?.minimumLength ?? passwordPolicy.defaultMinimumLength;

  // The panel keys this dialog on the user id, so a remount is the reset.
  function submit(event: FormEvent) {
    event.preventDefault();
    onSave({ ...draft, groups: splitList(groups, ",") });
  }

  return (
    <Modal
      closeDisabled={busy}
      eyebrow="User directory"
      onClose={onClose}
      title={user ? "Edit user" : "Add a user"}
      titleId="user-dialog-title"
    >
      <form className="dialog__form" onSubmit={submit}>
        <div className="dialog__body">
          <div className="form-grid">
            <label>
              <span>Full name</span>
              <input
                data-autofocus={user ? undefined : true}
                maxLength={120}
                onChange={(event) =>
                  setDraft({ ...draft, name: event.target.value })
                }
                required
                value={draft.name}
              />
            </label>
            <label>
              <span>Username</span>
              <input
                maxLength={64}
                onChange={(event) =>
                  setDraft({ ...draft, username: event.target.value })
                }
                required
                value={draft.username}
              />
            </label>
            <label className="form-grid__wide">
              <span>Email</span>
              <input
                maxLength={254}
                onChange={(event) =>
                  setDraft({ ...draft, email: event.target.value })
                }
                required
                type="email"
                value={draft.email}
              />
            </label>
            <label className="form-grid__wide">
              <span>Status</span>
              <select
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    status: event.target.value as UserDraft["status"],
                  })
                }
                value={draft.status}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <label className="form-grid__wide">
              <span>
                {user ? "New password" : "Temporary password"}{" "}
                {user && <em>Optional</em>}
              </span>
              <input
                autoComplete="new-password"
                maxLength={128}
                minLength={minimumPasswordLength}
                onChange={(event) =>
                  setDraft({ ...draft, password: event.target.value })
                }
                required={!user}
                type="password"
                value={draft.password}
              />
              <small>
                {minimumPasswordLength}–128 characters. Share through a secure
                channel.
              </small>
            </label>
            <label className="form-grid__wide">
              <span>
                Groups <em>Optional</em>
              </span>
              <input
                onChange={(event) => setGroups(event.target.value)}
                placeholder="mailbox:mailbox-uat@mail.stpwin.dev:admin"
                value={groups}
              />
              <small>
                Comma-separated exact values. Inbox grants use
                mailbox:&lt;mailbox-email&gt;:&lt;viewer|operator|admin&gt;.
              </small>
            </label>
          </div>
          {error && (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          )}
        </div>
        <footer className="dialog__actions">
          <span className="dialog__action-note">
            Changes apply immediately.
          </span>
          <button
            className="button button--secondary"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="button button--primary"
            disabled={busy}
            type="submit"
          >
            {busy ? "Saving…" : user ? "Save changes" : "Create user"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
